import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';
import { Button } from '../components/ui/button';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Fuel, DollarSign, TrendingUp, FileText, ArrowLeft, Building2, Droplets, Calendar, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { useVisibility } from '../contexts/VisibilityContext';

// Posto colors - modernized
const POSTO_COLORS = {
  'POSTO JP&HG LOBO': { bg: '#10B981', glow: 'shadow-emerald-500/20', accent: '#34d399' },
  'POSTO CARAUARI': { bg: '#EF4444', glow: 'shadow-red-500/20', accent: '#f87171' },
  'REDE BONS AMIGOS': { bg: '#3B82F6', glow: 'shadow-blue-500/20', accent: '#60a5fa' },
  'PONTÃO BARROTE': { bg: '#6B7280', glow: 'shadow-gray-500/20', accent: '#9ca3af' },
};

const CHART_COLORS = ['#06b6d4', '#3b82f6', '#6366f1', '#10b981', '#f59e0b', '#ef4444'];

const formatBR = (value) => value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatCompact = (value) => {
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return value.toFixed(0);
};

// Mini sparkline component
function Sparkline({ data, color }) {
  return (
    <ResponsiveContainer width="100%" height={40}>
      <LineChart data={data}>
        <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// Glass card wrapper
function GlassCard({ children, className = '', onClick, testId, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className={`relative overflow-hidden rounded-2xl bg-slate-900/50 backdrop-blur-xl border border-white/5 shadow-2xl ${onClick ? 'cursor-pointer hover:bg-slate-800/60 hover:-translate-y-1 hover:shadow-xl hover:border-slate-700/50' : ''} transition-all duration-300 ${className}`}
      onClick={onClick}
      data-testid={testId}
    >
      {children}
    </motion.div>
  );
}

export default function DashboardPage() {
  const { mask } = useVisibility();
  const [postos, setPostos] = useState([]);
  const [abastecimentos, setAbastecimentos] = useState([]);
  const [secretarias, setSecretarias] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [combustiveis, setCombustiveis] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedPosto, setSelectedPosto] = useState(null);
  const [selectedSecretaria, setSelectedSecretaria] = useState(null);
  const [themeColor, setThemeColor] = useState('#06b6d4');
  
  const now = new Date();
  const [dataInicio, setDataInicio] = useState(() => new Date(now.getFullYear(), now.getMonth(), 1));
  const [dataFim, setDataFim] = useState(() => new Date(now.getFullYear(), now.getMonth() + 1, 0));
  const [openInicio, setOpenInicio] = useState(false);
  const [openFim, setOpenFim] = useState(false);

  useEffect(() => { fetchAllData(); }, []);

  const abastecimentosFiltradosPorPeriodo = useMemo(() => {
    if (!dataInicio || !dataFim) return abastecimentos;
    const inicioStr = format(dataInicio, 'yyyy-MM-dd');
    const fimStr = format(dataFim, 'yyyy-MM-dd');
    return abastecimentos.filter(a => a.date && a.date >= inicioStr && a.date <= fimStr);
  }, [abastecimentos, dataInicio, dataFim]);

  const getAbastecimentosFiltrados = () => abastecimentosFiltradosPorPeriodo;

  const fetchAllData = async () => {
    try {
      const [postosRes, abastRes, secretariasRes, funcionariosRes, combustiveisRes] = await Promise.all([
        api.get('/postos'),
        api.get('/abastecimentos'),
        api.get('/secretarias'),
        api.get('/funcionarios'),
        api.get('/combustiveis')
      ]);
      setPostos(postosRes.data);
      setAbastecimentos(abastRes.data);
      setSecretarias(secretariasRes.data);
      setFuncionarios(funcionariosRes.data);
      setCombustiveis(combustiveisRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const getPostoStats = () => {
    const stats = {};
    const abastFiltrados = getAbastecimentosFiltrados();
    postos.forEach(posto => {
      stats[posto.name] = { name: posto.name, totalValue: 0, totalLiters: 0, count: 0, id: posto.id };
    });
    abastFiltrados.forEach(abast => {
      const posto = postos.find(p => p.id === abast.posto_id);
      if (posto && stats[posto.name]) {
        stats[posto.name].totalValue += abast.total_value || 0;
        stats[posto.name].totalLiters += abast.total_quantity || 0;
        stats[posto.name].count += 1;
      }
    });
    return Object.values(stats).sort((a, b) => b.totalValue - a.totalValue);
  };

  // Chart data: consumption by day
  const getConsumptionTrend = () => {
    const abastFiltrados = getAbastecimentosFiltrados();
    const byDate = {};
    abastFiltrados.forEach(a => {
      if (!a.date) return;
      const d = a.date;
      if (!byDate[d]) byDate[d] = { date: d, valor: 0, litros: 0 };
      byDate[d].valor += a.total_value || 0;
      byDate[d].litros += a.total_quantity || 0;
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date)).map(d => ({
      ...d,
      label: d.date.split('-').slice(1).reverse().join('/')
    }));
  };

  // Chart data: top secretarias
  const getTopSecretarias = () => {
    const abastFiltrados = getAbastecimentosFiltrados();
    const stats = {};
    abastFiltrados.forEach(abast => {
      const func = funcionarios.find(f => f.id === abast.funcionario_id);
      if (func && func.secretaria_id) {
        const sec = secretarias.find(s => s.id === func.secretaria_id);
        if (sec) {
          if (!stats[sec.name]) stats[sec.name] = { name: sec.name, valor: 0 };
          stats[sec.name].valor += abast.total_value || 0;
        }
      }
    });
    return Object.values(stats).sort((a, b) => b.valor - a.valor).slice(0, 6).map(s => ({
      ...s, shortName: s.name.length > 12 ? s.name.substring(0, 12) + '...' : s.name
    }));
  };

  // Chart data: fuel type distribution
  const getFuelDistribution = () => {
    const abastFiltrados = getAbastecimentosFiltrados();
    const stats = {};
    abastFiltrados.forEach(abast => {
      (abast.items || []).forEach(item => {
        const comb = combustiveis.find(c => c.id === item.combustivel_id);
        const name = comb ? comb.type : 'Outro';
        if (!stats[name]) stats[name] = { name, value: 0 };
        stats[name].value += item.total_value || 0;
      });
    });
    return Object.values(stats).sort((a, b) => b.value - a.value);
  };

  // Sparkline mock data (from actual totals)
  const getSparkData = (field) => {
    const abastFiltrados = getAbastecimentosFiltrados();
    const byDate = {};
    abastFiltrados.forEach(a => {
      if (!a.date) return;
      if (!byDate[a.date]) byDate[a.date] = 0;
      byDate[a.date] += a[field] || 0;
    });
    return Object.entries(byDate).sort().map(([, v]) => ({ value: v }));
  };

  // Secretarias ranking for posto detail
  const getSecretariasRanking = () => {
    if (!selectedPosto) return [];
    const posto = postos.find(p => p.name === selectedPosto);
    if (!posto) return [];
    const secretariaStats = {};
    const abastFiltrados = getAbastecimentosFiltrados();
    abastFiltrados.filter(a => a.posto_id === posto.id).forEach(abast => {
      const func = funcionarios.find(f => f.id === abast.funcionario_id);
      if (func && func.secretaria_id) {
        const secretaria = secretarias.find(s => s.id === func.secretaria_id);
        if (secretaria) {
          if (!secretariaStats[secretaria.id]) {
            secretariaStats[secretaria.id] = { id: secretaria.id, name: secretaria.name, totalValue: 0, totalLiters: 0, items: [] };
          }
          secretariaStats[secretaria.id].totalValue += abast.total_value || 0;
          secretariaStats[secretaria.id].totalLiters += abast.total_quantity || 0;
          if (abast.items) secretariaStats[secretaria.id].items.push(...abast.items);
        }
      }
    });
    return Object.values(secretariaStats).sort((a, b) => b.totalValue - a.totalValue);
  };

  const getFuelBreakdown = () => {
    if (!selectedSecretaria) return [];
    const secretariaData = getSecretariasRanking().find(s => s.id === selectedSecretaria);
    if (!secretariaData) return [];
    const fuelStats = {};
    secretariaData.items.forEach(item => {
      const comb = combustiveis.find(c => c.id === item.combustivel_id);
      const fuelName = comb ? comb.type : 'Desconhecido';
      if (!fuelStats[fuelName]) fuelStats[fuelName] = { name: fuelName, liters: 0, value: 0 };
      fuelStats[fuelName].liters += item.quantity || 0;
      fuelStats[fuelName].value += item.total_value || 0;
    });
    return Object.values(fuelStats).sort((a, b) => b.liters - a.liters);
  };

  const handlePostoClick = (postoName) => {
    setSelectedPosto(postoName);
    setSelectedSecretaria(null);
    const colors = POSTO_COLORS[postoName] || { bg: '#06b6d4' };
    setThemeColor(colors.bg);
  };

  const handleSecretariaClick = (secretariaId) => setSelectedSecretaria(secretariaId);

  const handleBack = () => {
    if (selectedSecretaria) { setSelectedSecretaria(null); }
    else if (selectedPosto) { setSelectedPosto(null); setThemeColor('#06b6d4'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96 bg-[#09090b] rounded-2xl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mx-auto"></div>
          <p className="mt-4 text-slate-400">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  const postoStats = getPostoStats();
  const totalValue = postoStats.reduce((acc, p) => acc + p.totalValue, 0);
  const totalLiters = postoStats.reduce((acc, p) => acc + p.totalLiters, 0);
  const totalCount = postoStats.reduce((acc, p) => acc + p.count, 0);
  const trendData = getConsumptionTrend();
  const topSecretarias = getTopSecretarias();
  const fuelDist = getFuelDistribution();

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 shadow-xl">
          <p className="text-xs text-slate-400">{label}</p>
          {payload.map((p, i) => (
            <p key={i} className="text-sm font-semibold text-white">R$ {formatBR(p.value)}</p>
          ))}
        </div>
      );
    }
    return null;
  };

  // ==================== MAIN VIEW ====================
  if (!selectedPosto) {
    const periodoLabel = dataInicio && dataFim
      ? `${format(dataInicio, 'dd/MM/yyyy')} - ${format(dataFim, 'dd/MM/yyyy')}`
      : 'Selecione';

    return (
      <div className="min-h-full bg-[#09090b] -m-6 p-6 sm:p-8 space-y-6" data-testid="dashboard-page">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-5 border-b border-white/5">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Controle de Abastecimento
            </h1>
            <p className="text-sm text-slate-400 mt-1">Prefeitura Municipal de Carauari</p>
          </div>
          
          <div className="flex items-center gap-2 bg-slate-900/60 border border-slate-800 rounded-xl px-3 py-2" data-testid="date-range-selector">
            <Calendar size={16} className="text-cyan-400" />
            <Popover open={openInicio} onOpenChange={setOpenInicio}>
              <PopoverTrigger asChild>
                <Button variant="ghost" className="h-8 px-2 text-xs text-slate-300 hover:text-white hover:bg-slate-800" data-testid="date-start-picker">
                  {dataInicio ? format(dataInicio, 'dd/MM/yyyy', { locale: ptBR }) : 'Início'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-700" align="end">
                <CalendarComponent mode="single" selected={dataInicio} onSelect={(date) => { setDataInicio(date); setOpenInicio(false); }} locale={ptBR} initialFocus />
              </PopoverContent>
            </Popover>
            <span className="text-slate-500 text-xs">-</span>
            <Popover open={openFim} onOpenChange={setOpenFim}>
              <PopoverTrigger asChild>
                <Button variant="ghost" className="h-8 px-2 text-xs text-slate-300 hover:text-white hover:bg-slate-800" data-testid="date-end-picker">
                  {dataFim ? format(dataFim, 'dd/MM/yyyy', { locale: ptBR }) : 'Fim'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-700" align="end">
                <CalendarComponent mode="single" selected={dataFim} onSelect={(date) => { setDataFim(date); setOpenFim(false); }} locale={ptBR} disabled={(date) => dataInicio && date < dataInicio} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
        </motion.div>

        {/* Metric Cards (North Stars) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          <GlassCard delay={0.1} testId="metric-value">
            <div className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold tracking-wider uppercase text-slate-500">Valor Total</span>
                <DollarSign size={18} className="text-cyan-400" />
              </div>
              <p className="text-3xl font-bold tracking-tighter text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                R$ {mask(formatBR(totalValue))}
              </p>
              <div className="mt-3 h-10">
                <Sparkline data={getSparkData('total_value')} color="#06b6d4" />
              </div>
            </div>
          </GlassCard>

          <GlassCard delay={0.2} testId="metric-liters">
            <div className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold tracking-wider uppercase text-slate-500">Total Litros</span>
                <Fuel size={18} className="text-blue-400" />
              </div>
              <p className="text-3xl font-bold tracking-tighter text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {mask(formatBR(totalLiters))} L
              </p>
              <div className="mt-3 h-10">
                <Sparkline data={getSparkData('total_quantity')} color="#3b82f6" />
              </div>
            </div>
          </GlassCard>

          <GlassCard delay={0.3} testId="metric-count">
            <div className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold tracking-wider uppercase text-slate-500">Abastecimentos</span>
                <FileText size={18} className="text-indigo-400" />
              </div>
              <p className="text-3xl font-bold tracking-tighter text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {totalCount}
              </p>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-slate-500">{periodoLabel}</span>
              </div>
            </div>
          </GlassCard>
        </div>

        {/* Postos Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {postoStats.map((posto, idx) => {
            const colors = POSTO_COLORS[posto.name] || { bg: '#06b6d4', glow: 'shadow-cyan-500/20', accent: '#06b6d4' };
            return (
              <GlassCard
                key={posto.id || posto.name}
                delay={0.1 + idx * 0.1}
                onClick={() => handlePostoClick(posto.name)}
                testId={`posto-card-${posto.name.replace(/\s+/g, '-').toLowerCase()}`}
                className={`group ${colors.glow}`}
              >
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${colors.bg}20` }}>
                      <Fuel size={20} style={{ color: colors.bg }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{posto.name}</p>
                      <p className="text-xs text-slate-500">{posto.count} registros</p>
                    </div>
                    <ChevronRight size={16} className="text-slate-600 group-hover:text-slate-300 transition-colors" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs text-slate-500">Valor</span>
                      <span className="text-lg font-bold text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        R$ {mask(formatBR(posto.totalValue))}
                      </span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs text-slate-500">Litros</span>
                      <span className="text-sm font-medium text-slate-300">
                        {mask(formatBR(posto.totalLiters))} L
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden mt-2">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${totalValue > 0 ? (posto.totalValue / totalValue * 100) : 0}%`, backgroundColor: colors.bg }}></div>
                    </div>
                  </div>
                </div>
              </GlassCard>
            );
          })}
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Area Chart - Consumption Trend */}
          <GlassCard delay={0.5} className="lg:col-span-2">
            <div className="p-5">
              <h3 className="text-sm font-semibold text-slate-200 mb-4 flex items-center gap-2">
                <TrendingUp size={16} className="text-cyan-400" /> Consumo no Periodo
              </h3>
              <div className="h-52">
                {trendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendData}>
                      <defs>
                        <linearGradient id="gradCyan" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#1e293b' }} />
                      <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#1e293b' }} tickFormatter={formatCompact} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="valor" stroke="#06b6d4" strokeWidth={2} fill="url(#gradCyan)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-500 text-sm">Sem dados no periodo</div>
                )}
              </div>
            </div>
          </GlassCard>

          {/* Donut Chart - Fuel Distribution */}
          <GlassCard delay={0.6}>
            <div className="p-5">
              <h3 className="text-sm font-semibold text-slate-200 mb-4 flex items-center gap-2">
                <Droplets size={16} className="text-indigo-400" /> Combustiveis
              </h3>
              <div className="h-52">
                {fuelDist.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={fuelDist} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value" stroke="none">
                        {fuelDist.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={({ active, payload }) => active && payload && payload[0] ? (
                        <div className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 shadow-xl">
                          <p className="text-xs text-slate-400">{payload[0].name}</p>
                          <p className="text-sm font-semibold text-white">R$ {formatBR(payload[0].value)}</p>
                        </div>
                      ) : null} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-500 text-sm">Sem dados</div>
                )}
              </div>
              {/* Legend */}
              <div className="flex flex-wrap gap-2 mt-2">
                {fuelDist.slice(0, 4).map((f, i) => (
                  <span key={f.name} className="flex items-center gap-1.5 text-xs text-slate-400">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CHART_COLORS[i] }}></span>
                    {f.name}
                  </span>
                ))}
              </div>
            </div>
          </GlassCard>
        </div>

        {/* Bar Chart - Top Secretarias */}
        <GlassCard delay={0.7}>
          <div className="p-5">
            <h3 className="text-sm font-semibold text-slate-200 mb-4 flex items-center gap-2">
              <Building2 size={16} className="text-blue-400" /> Top Secretarias
            </h3>
            <div className="h-48">
              {topSecretarias.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topSecretarias} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#1e293b' }} tickFormatter={formatCompact} />
                    <YAxis type="category" dataKey="shortName" tick={{ fontSize: 11, fill: '#94a3b8' }} width={100} axisLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="valor" radius={[0, 6, 6, 0]} fill="#3b82f6">
                      {topSecretarias.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-500 text-sm">Sem dados no periodo</div>
              )}
            </div>
          </div>
        </GlassCard>
      </div>
    );
  }

  // ==================== POSTO DETAIL VIEW ====================
  if (selectedPosto && !selectedSecretaria) {
    const postoData = postoStats.find(p => p.name === selectedPosto) || { totalValue: 0, totalLiters: 0 };
    const secretariasRanking = getSecretariasRanking();
    const periodoLabel = dataInicio && dataFim ? `${format(dataInicio, 'dd/MM/yyyy')} - ${format(dataFim, 'dd/MM/yyyy')}` : '';

    return (
      <div className="min-h-full bg-[#09090b] -m-6 p-6 sm:p-8 space-y-6" data-testid="dashboard-posto-detail">
        {/* Header */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-4">
          <Button variant="ghost" onClick={handleBack} className="text-slate-400 hover:text-white hover:bg-slate-800 border border-slate-800" data-testid="back-btn">
            <ArrowLeft size={16} /> Voltar
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-white">{selectedPosto}</h2>
            <p className="text-sm text-slate-500">Ranking de secretarias por consumo</p>
          </div>
        </motion.div>

        {/* Date Selector */}
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-900/60 border border-slate-800" data-testid="posto-date-selector">
          <Calendar size={16} className="text-cyan-400" />
          <Popover open={openInicio} onOpenChange={setOpenInicio}>
            <PopoverTrigger asChild>
              <Button variant="ghost" className="h-8 px-2 text-xs text-slate-300 hover:text-white hover:bg-slate-800" data-testid="posto-date-start">
                {dataInicio ? format(dataInicio, 'dd/MM/yyyy', { locale: ptBR }) : 'Inicio'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-700" align="start">
              <CalendarComponent mode="single" selected={dataInicio} onSelect={(date) => { setDataInicio(date); setOpenInicio(false); }} locale={ptBR} initialFocus />
            </PopoverContent>
          </Popover>
          <span className="text-slate-600">-</span>
          <Popover open={openFim} onOpenChange={setOpenFim}>
            <PopoverTrigger asChild>
              <Button variant="ghost" className="h-8 px-2 text-xs text-slate-300 hover:text-white hover:bg-slate-800" data-testid="posto-date-end">
                {dataFim ? format(dataFim, 'dd/MM/yyyy', { locale: ptBR }) : 'Fim'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-700" align="start">
              <CalendarComponent mode="single" selected={dataFim} onSelect={(date) => { setDataFim(date); setOpenFim(false); }} locale={ptBR} disabled={(date) => dataInicio && date < dataInicio} initialFocus />
            </PopoverContent>
          </Popover>
          <span className="ml-auto text-xs text-slate-500">{periodoLabel}</span>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <GlassCard delay={0.1}>
            <div className="p-5">
              <span className="text-xs uppercase tracking-wider text-slate-500">Valor Total</span>
              <p className="text-2xl font-bold text-white mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>R$ {mask(formatBR(postoData.totalValue))}</p>
            </div>
          </GlassCard>
          <GlassCard delay={0.2}>
            <div className="p-5">
              <span className="text-xs uppercase tracking-wider text-slate-500">Total Litros</span>
              <p className="text-2xl font-bold text-white mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{mask(formatBR(postoData.totalLiters))} L</p>
            </div>
          </GlassCard>
          <GlassCard delay={0.3}>
            <div className="p-5">
              <span className="text-xs uppercase tracking-wider text-slate-500">Secretarias</span>
              <p className="text-2xl font-bold text-white mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{secretariasRanking.length}</p>
            </div>
          </GlassCard>
        </div>

        {/* Ranking */}
        <GlassCard delay={0.4}>
          <div className="p-5">
            <h3 className="text-sm font-semibold text-slate-200 mb-4 flex items-center gap-2">
              <Building2 size={16} style={{ color: themeColor }} /> Ranking de Secretarias
            </h3>
            {secretariasRanking.length === 0 ? (
              <p className="text-slate-500 text-center py-8">Nenhum abastecimento neste posto no periodo</p>
            ) : (
              <div className="space-y-2">
                {secretariasRanking.map((sec, index) => {
                  const percentage = totalValue > 0 ? (sec.totalValue / postoData.totalValue * 100) : 0;
                  return (
                    <div
                      key={sec.id}
                      className="flex items-center gap-4 p-3 rounded-xl hover:bg-slate-800/60 cursor-pointer transition-all group"
                      onClick={() => handleSecretariaClick(sec.id)}
                    >
                      <span className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold" style={{ backgroundColor: `${themeColor}20`, color: themeColor }}>
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{sec.name}</p>
                        <div className="w-full h-1.5 bg-slate-800 rounded-full mt-1.5 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${percentage}%`, backgroundColor: themeColor }}></div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>R$ {mask(formatBR(sec.totalValue))}</p>
                        <p className="text-xs text-slate-500">{mask(formatBR(sec.totalLiters))} L</p>
                      </div>
                      <ChevronRight size={14} className="text-slate-600 group-hover:text-slate-300" />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </GlassCard>
      </div>
    );
  }

  // ==================== SECRETARIA DETAIL VIEW ====================
  if (selectedPosto && selectedSecretaria) {
    const secretariasRanking = getSecretariasRanking();
    const secretariaData = secretariasRanking.find(s => s.id === selectedSecretaria);
    const secretariaName = secretariaData?.name || 'Secretaria';
    const fuelBreakdown = getFuelBreakdown();

    return (
      <div className="min-h-full bg-[#09090b] -m-6 p-6 sm:p-8 space-y-6" data-testid="dashboard-secretaria-detail">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-4">
          <Button variant="ghost" onClick={handleBack} className="text-slate-400 hover:text-white hover:bg-slate-800 border border-slate-800">
            <ArrowLeft size={16} /> Voltar
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-white">{secretariaName}</h2>
            <p className="text-sm text-slate-500">Combustiveis - {selectedPosto}</p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GlassCard delay={0.1}>
            <div className="p-5">
              <span className="text-xs uppercase tracking-wider text-slate-500">Valor Total</span>
              <p className="text-2xl font-bold text-white mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>R$ {mask(formatBR(secretariaData?.totalValue || 0))}</p>
            </div>
          </GlassCard>
          <GlassCard delay={0.2}>
            <div className="p-5">
              <span className="text-xs uppercase tracking-wider text-slate-500">Total Litros</span>
              <p className="text-2xl font-bold text-white mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{mask(formatBR(secretariaData?.totalLiters || 0))} L</p>
            </div>
          </GlassCard>
        </div>

        <GlassCard delay={0.3}>
          <div className="p-5">
            <h3 className="text-sm font-semibold text-slate-200 mb-4 flex items-center gap-2">
              <Droplets size={16} style={{ color: themeColor }} /> Combustiveis
            </h3>
            {fuelBreakdown.length === 0 ? (
              <p className="text-slate-500 text-center py-8">Nenhum combustivel</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {fuelBreakdown.map((fuel, index) => (
                  <div key={fuel.name} className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}></div>
                      <p className="text-sm font-medium text-white">{fuel.name}</p>
                    </div>
                    <p className="text-xl font-bold text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{mask(formatBR(fuel.liters))} L</p>
                    <p className="text-sm text-slate-400 mt-1">R$ {mask(formatBR(fuel.value))}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </GlassCard>
      </div>
    );
  }

  return null;
}
